import { inferReadonlyArray } from "@aprao/common/src/array/inferReadonlyArray.js";
import { pull } from "@aprao/common/src/array/pull.js";
import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { FRACTION_ONE } from "@aprao/common/src/math/fraction.js";
import { sum } from "@aprao/common/src/math/sum.js";
import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";

import { createTimelineOverride } from "../createTimelineOverride.js";
import type {
  OtherCostKeys,
  ShapeOtherCost,
  ShapeOtherCostsGroup,
} from "../shapes/ShapeOtherCostsGroup.js";
import { WithNameDelegate } from "./delegates/WithNameDelegate.js";
import { WithProjectionPeriodsDelegate } from "./delegates/WithProjectionPeriodsDelegate.js";
import { WithRelativesSetDelegate } from "./delegates/WithRelativesSetDelegate.js";
import {
  type NominatorProvidersByKey,
  withAbsoluteValuePredicate,
} from "./RelativesSetFacade.js";

const createCost = (ordinal: number): ShapeOtherCost => ({
  name: `Other cost ${ordinal}`,
  relatives: {
    absolute: null,
    ratioBuildCostGlobalTotal: null,
    ratioGdv: null,
  },
  projectionPeriods: [],
  timelineOverride: createTimelineOverride(),
});

export const withCostsPredicate = (group: OtherCostsGroupFacade) =>
  group.costs.some(({ relativesSet }) =>
    withAbsoluteValuePredicate(relativesSet),
  );

export class OtherCostFacade {
  readonly $model;

  readonly _providers;

  constructor(
    model: ShapeOtherCost,
    providers: NominatorProvidersByKey<OtherCostKeys>,
  ) {
    makeAutoObservable(this);

    this.$model = model;
    this._providers = providers;
  }

  get relativesSet() {
    return new WithRelativesSetDelegate(this.$model, this._providers);
  }

  get named() {
    return new WithNameDelegate(this.$model);
  }

  get projectionPeriods() {
    return new WithProjectionPeriodsDelegate(this.$model);
  }
}

export class OtherCostsGroupFacade {
  readonly $model;

  readonly _providers;

  constructor(
    model: ShapeOtherCostsGroup,
    providers: {
      ratioBuildCostGlobalTotal: () => BigFraction | null;
      ratioGdv: () => BigFraction | null;
    },
  ) {
    makeAutoObservable(this);

    this.$model = model;
    this._providers = providers;
  }

  get name() {
    return this.$model.name;
  }

  readonly _decorateCost = computedFn(
    (cost: ShapeOtherCost) =>
      new OtherCostFacade(cost, {
        ...this._providers,
        absolute: () => FRACTION_ONE,
      }),
  );

  get costs() {
    return inferReadonlyArray(
      this.$model.costs.map((cost) => this._decorateCost(cost)),
    );
  }

  get costsTotal() {
    return sum(
      this.costs.map((cost) => cost.relativesSet.getRelative("absolute")),
    );
  }

  set name(name: string) {
    this.$model.name = name;
  }

  addCost() {
    this.$model.costs.push(createCost(this.$model.costs.length + 1));
  }

  removeCost(cost: OtherCostFacade) {
    pull(this.$model.costs, cost.$model);
  }
}
